import { t } from '../i18n';
import {
  applyPolicyToInputs,
  validatePassword,
  type PasswordPolicy,
  type PasswordPolicyErrorCode,
  type ValidatePasswordOptions,
} from '../crypto/passwordPolicy';

const RULE_OK = 'password-policy-hint__rule--ok';
const RULE_FAIL = 'password-policy-hint__rule--fail';

type BoundHandlers = {
  onBlur: () => void;
  onInput: () => void;
};

const boundPasswordInputs = new WeakMap<HTMLInputElement, BoundHandlers>();

export type PasswordPolicyRequirementId =
  | 'length'
  | 'uppercase'
  | 'lowercase'
  | 'digit'
  | 'special'
  | 'classes'
  | 'common'
  | 'email'
  | 'banned'
  | 'repeated'
  | 'sequential'
  | 'maxAge';

export interface PasswordPolicyRequirement {
  id: PasswordPolicyRequirementId;
  /** Error codes that mark this rule as failed. Null = informational only (no ok/fail). */
  errorCodes: PasswordPolicyErrorCode[] | null;
  text: string;
}

export function passwordPolicyErrorMessage(code: PasswordPolicyErrorCode, policy: PasswordPolicy): string {
  switch (code) {
    case 'too_short':
      return t('passwordPolicy.errors.too_short', { min: String(policy.minLength) });
    case 'too_long':
      return t('passwordPolicy.errors.too_long', { max: String(policy.maxLength) });
    case 'missing_uppercase':
      return t('passwordPolicy.errors.missing_uppercase');
    case 'missing_lowercase':
      return t('passwordPolicy.errors.missing_lowercase');
    case 'missing_digit':
      return t('passwordPolicy.errors.missing_digit');
    case 'missing_special':
      return t('passwordPolicy.errors.missing_special');
    case 'too_few_classes':
      return t('passwordPolicy.errors.too_few_classes', { min: String(policy.minCharacterClasses) });
    case 'banned_word':
      return t('passwordPolicy.errors.banned_word');
    case 'common_password':
      return t('passwordPolicy.errors.common_password');
    case 'contains_email':
      return t('passwordPolicy.errors.contains_email');
    case 'repeated_chars':
      return t('passwordPolicy.errors.repeated_chars', { max: String(policy.maxRepeatedChars ?? '') });
    case 'sequential_chars':
      return t('passwordPolicy.errors.sequential_chars', { max: String(policy.maxSequentialChars ?? '') });
    default:
      return t('passwordPolicy.errors.generic');
  }
}

export function passwordPolicyErrorFromCause(cause: unknown, policy: PasswordPolicy): string | null {
  if (!(cause instanceof Error)) {
    return null;
  }
  const code = cause.message as PasswordPolicyErrorCode;
  const known: PasswordPolicyErrorCode[] = [
    'too_short', 'too_long', 'missing_uppercase', 'missing_lowercase', 'missing_digit',
    'missing_special', 'too_few_classes', 'banned_word', 'common_password', 'contains_email',
    'repeated_chars', 'sequential_chars',
  ];
  if (!known.includes(code)) {
    return null;
  }
  return passwordPolicyErrorMessage(code, policy);
}

function enabledRequireCount(policy: PasswordPolicy): number {
  return [
    policy.requireUppercase,
    policy.requireLowercase,
    policy.requireDigit,
    policy.requireSpecial,
  ].filter(Boolean).length;
}

/** True when minCharacterClasses is already implied by the individual require* flags. */
export function isCharacterClassesRequirementRedundant(policy: PasswordPolicy): boolean {
  return policy.minCharacterClasses > 0
    && policy.minCharacterClasses <= enabledRequireCount(policy);
}

export function listPasswordPolicyRequirements(policy: PasswordPolicy): PasswordPolicyRequirement[] {
  const requirements: PasswordPolicyRequirement[] = [
    {
      id: 'length',
      errorCodes: ['too_short', 'too_long'],
      text: t('passwordPolicy.requirements.length', {
        min: String(policy.minLength),
        max: String(policy.maxLength),
      }),
    },
  ];

  if (policy.requireUppercase) {
    requirements.push({
      id: 'uppercase',
      errorCodes: ['missing_uppercase'],
      text: t('passwordPolicy.requirements.uppercase'),
    });
  }
  if (policy.requireLowercase) {
    requirements.push({
      id: 'lowercase',
      errorCodes: ['missing_lowercase'],
      text: t('passwordPolicy.requirements.lowercase'),
    });
  }
  if (policy.requireDigit) {
    requirements.push({
      id: 'digit',
      errorCodes: ['missing_digit'],
      text: t('passwordPolicy.requirements.digit'),
    });
  }
  if (policy.requireSpecial) {
    requirements.push({
      id: 'special',
      errorCodes: ['missing_special'],
      text: t('passwordPolicy.requirements.special'),
    });
  }
  if (policy.minCharacterClasses > 0 && !isCharacterClassesRequirementRedundant(policy)) {
    requirements.push({
      id: 'classes',
      errorCodes: ['too_few_classes'],
      text: t('passwordPolicy.requirements.classes', { min: String(policy.minCharacterClasses) }),
    });
  }
  if (policy.blockCommonPasswords) {
    requirements.push({
      id: 'common',
      errorCodes: ['common_password'],
      text: t('passwordPolicy.requirements.common'),
    });
  }
  if (policy.disallowEmailLocalPart) {
    requirements.push({
      id: 'email',
      errorCodes: ['contains_email'],
      text: t('passwordPolicy.requirements.email'),
    });
  }
  if (policy.bannedWords.length > 0) {
    requirements.push({
      id: 'banned',
      errorCodes: ['banned_word'],
      text: t('passwordPolicy.requirements.banned'),
    });
  }
  if (policy.maxRepeatedChars != null) {
    requirements.push({
      id: 'repeated',
      errorCodes: ['repeated_chars'],
      text: t('passwordPolicy.requirements.repeated', { max: String(policy.maxRepeatedChars) }),
    });
  }
  if (policy.maxSequentialChars != null) {
    requirements.push({
      id: 'sequential',
      errorCodes: ['sequential_chars'],
      text: t('passwordPolicy.requirements.sequential', { max: String(policy.maxSequentialChars) }),
    });
  }
  if (policy.maxAgeDays != null) {
    requirements.push({
      id: 'maxAge',
      errorCodes: null,
      text: t('passwordPolicy.requirements.maxAge', { days: String(policy.maxAgeDays) }),
    });
  }

  return requirements;
}

export function formatPasswordPolicyRequirements(policy: PasswordPolicy): string {
  return listPasswordPolicyRequirements(policy).map((rule) => rule.text).join(' ');
}

function clearRuleHighlights(list: HTMLElement): void {
  for (const item of list.querySelectorAll('li')) {
    item.classList.remove(RULE_OK, RULE_FAIL);
  }
}

function applyRuleHighlights(
  list: HTMLElement,
  requirements: PasswordPolicyRequirement[],
  errors: readonly PasswordPolicyErrorCode[],
): void {
  const errorSet = new Set(errors);
  for (const item of list.querySelectorAll('li')) {
    const id = item.getAttribute('data-rule') as PasswordPolicyRequirementId | null;
    const requirement = requirements.find((rule) => rule.id === id);
    item.classList.remove(RULE_OK, RULE_FAIL);
    if (!requirement?.errorCodes) {
      continue;
    }
    const failed = requirement.errorCodes.some((code) => errorSet.has(code));
    item.classList.add(failed ? RULE_FAIL : RULE_OK);
  }
}

function renderRequirementList(
  hint: HTMLElement,
  requirements: PasswordPolicyRequirement[],
): HTMLUListElement {
  hint.replaceChildren();
  const list = document.createElement('ul');
  list.className = 'password-policy-hint';
  for (const requirement of requirements) {
    const item = document.createElement('li');
    item.className = 'password-policy-hint__rule';
    item.dataset.rule = requirement.id;
    item.textContent = requirement.text;
    list.appendChild(item);
  }
  hint.appendChild(list);
  return list;
}

export function bindPasswordPolicyToForm(input: {
  policy: PasswordPolicy;
  inputs: Array<HTMLInputElement | null | undefined>;
  hint?: HTMLElement | null;
  email?: string | null;
}): void {
  applyPolicyToInputs(input.inputs, input.policy);

  const passwordInput = input.inputs.find((el): el is HTMLInputElement => !!el) ?? null;
  if (passwordInput) {
    const previous = boundPasswordInputs.get(passwordInput);
    if (previous) {
      passwordInput.removeEventListener('blur', previous.onBlur);
      passwordInput.removeEventListener('input', previous.onInput);
      boundPasswordInputs.delete(passwordInput);
    }
  }

  if (!input.hint) {
    return;
  }

  const requirements = listPasswordPolicyRequirements(input.policy);
  const list = renderRequirementList(input.hint, requirements);
  input.hint.hidden = false;

  if (!passwordInput) {
    return;
  }

  const onBlur = () => {
    const result = validatePassword(passwordInput.value, input.policy, { email: input.email });
    applyRuleHighlights(list, requirements, result.errors);
  };
  const onInput = () => {
    clearRuleHighlights(list);
  };

  passwordInput.addEventListener('blur', onBlur);
  passwordInput.addEventListener('input', onInput);
  boundPasswordInputs.set(passwordInput, { onBlur, onInput });
}

export function validatePasswordForUi(
  password: string,
  policy: PasswordPolicy,
  options: ValidatePasswordOptions = {},
): string | null {
  const result = validatePassword(password, policy, options);
  if (result.ok) {
    return null;
  }
  return passwordPolicyErrorMessage(result.errors[0]!, policy);
}
